/**
 * Sales metrics page.
 *
 * Four tabs over one shared filter row: Company, Teams (product lines), Reps, and a
 * rep drill-down. Scope is enforced server-side by the RPCs
 * (private.dashboard_data_worker_ids: roles 1/2 see everything, role 3 sees their
 * teams, everyone else sees themselves), so the client gating below is presentation
 * only - a rep loading this page gets their own numbers, not an empty screen.
 *
 * Data notes that shape what is shown:
 *   - There is no knock/visit table. A "recorded door" is a row in `customers`, so
 *     every rate is "of recorded doors" and the denominator is always displayed.
 *   - Rates are null (not 0) when there is no denominator; null renders as "—".
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "flowbite-react";
import { HiExclamation, HiInformationCircle } from "react-icons/hi";

import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { MetricTooltip } from "../../components/sales-metrics/MetricTooltip";
import {
  getAllMetrics,
  type AllMetrics,
  type Grain,
  type RepMetrics,
} from "../../services/sales-metrics";
import { defaultFilter, type MetricsFilter } from "../../components/sales-metrics/scope";
import { MetricsToolbar } from "../../components/sales-metrics/MetricsToolbar";
import { useAuth } from "../../contexts/AuthContext";
import {
  getSavedViews,
  saveView,
  deleteView,
  setDefaultView,
  resolveView,
  type MetricsSavedView,
} from "../../services/metrics-presets";
import {
  HeroFigure,
  StatTile,
  Meter,
  ChartCard,
} from "../../components/sales-metrics/primitives";
import {
  FunnelChart,
  TrendChart,
  RankedBar,
  DivergingBar,
  ProductMixBar,
  ChartWithTable,
} from "../../components/sales-metrics/charts";
import {
  RepTable,
  MIN_DOORS_FOR_RANKING,
  isCloseRateArtifact,
} from "../../components/sales-metrics/RepTable";
import { TeamScorecard } from "../../components/sales-metrics/TeamScorecard";
import {
  formatCurrency,
  formatNumber,
  formatRate,
  median,
} from "../../components/sales-metrics/format";
import "../../components/sales-metrics/viz-tokens.css";

const TABS = ["Unternehmen", "Produktlinien", "Vertriebler", "Einzelanalyse"] as const;

function parseFilterFromUrl(params: URLSearchParams): MetricsFilter {
  const fallback = defaultFilter();
  const start = params.get("start");
  const end = params.get("end");
  const grain = params.get("grain") as Grain | null;

  const isDate = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);

  return {
    start: isDate(start) ? start : fallback.start,
    end: isDate(end) ? end : fallback.end,
    grain: grain === "day" || grain === "week" || grain === "month" ? grain : fallback.grain,
  };
}

export default function SalesMetricsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<MetricsFilter>(() => parseFilterFromUrl(searchParams));
  const [data, setData] = useState<AllMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRepId, setSelectedRepId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const { user } = useAuth();
  const [views, setViews] = useState<MetricsSavedView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);

  // Guards against a slow earlier request overwriting a newer one's result.
  const requestRef = useRef(0);

  const applyFilter = useCallback(
    (next: MetricsFilter) => {
      setFilter(next);
      setSearchParams(
        { start: next.start, end: next.end, grain: next.grain },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    const requestId = ++requestRef.current;
    setLoading(true);

    getAllMetrics(filter.start, filter.end, filter.grain)
      .then((result) => {
        if (requestRef.current !== requestId) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (requestRef.current !== requestId) return;
        // Surface the real failure. The old page collapsed every error into 0,
        // so a broken RPC was indistinguishable from a genuine zero.
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (requestRef.current !== requestId) return;
        setLoading(false);
      });
  }, [filter]);

  // Saved views: load this user's list, and apply their default on first mount only
  // if the URL carries no explicit scope (a shared link must win over a default).
  const hasUrlScope = searchParams.has("start") || searchParams.has("end");
  const appliedDefaultRef = useRef(false);

  useEffect(() => {
    if (!user?.id) return;
    const saved = getSavedViews(user.id);
    setViews(saved);

    if (appliedDefaultRef.current || hasUrlScope) return;
    appliedDefaultRef.current = true;
    const fallback = saved.find((v) => v.isDefault);
    if (fallback) {
      setActiveViewId(fallback.id);
      setActiveTab(fallback.tab);
      applyFilter(resolveView(fallback));
    }
  }, [user?.id, hasUrlScope, applyFilter]);

  const handleSelectView = useCallback(
    (view: MetricsSavedView) => {
      setActiveViewId(view.id);
      setActiveTab(view.tab);
      applyFilter(resolveView(view));
    },
    [applyFilter]
  );

  const handleSaveView = useCallback(
    (name: string, isDefault: boolean) => {
      if (!user?.id) return;
      const created = saveView(user.id, name, filter, activeTab, isDefault);
      setViews(getSavedViews(user.id));
      setActiveViewId(created.id);
    },
    [user?.id, filter, activeTab]
  );

  const handleDeleteView = useCallback(
    (id: string) => {
      if (!user?.id) return;
      deleteView(user.id, id);
      setViews(getSavedViews(user.id));
      setActiveViewId((cur) => (cur === id ? null : cur));
    },
    [user?.id]
  );

  const handleSetDefaultView = useCallback(
    (id: string) => {
      if (!user?.id) return;
      setDefaultView(user.id, id);
      setViews(getSavedViews(user.id));
    },
    [user?.id]
  );

  const company = data?.company ?? null;
  const teams = useMemo(() => data?.teams ?? [], [data]);
  const reps = useMemo(() => data?.reps ?? [], [data]);
  const timeseries = useMemo(() => data?.timeseries ?? [], [data]);

  const selectedRep = useMemo(
    () => reps.find((r) => r.workerId === selectedRepId) ?? null,
    [reps, selectedRepId]
  );

  const unpriced = company?.unpricedContracts ?? 0;

  return (
    <NavbarSidebarLayout pageTitle="Vertriebs-Metriken">
      <div className="viz-root px-2 pt-4">
        {/* Title and scope share one line: the page is a scanning surface, so the
            chrome above the first number is kept to a single row. */}
        <header className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Vertriebs-Metriken
          </h1>
          <MetricsToolbar
            filter={filter}
            onChange={applyFilter}
            stale={loading && !!data}
            views={views}
            activeViewId={activeViewId}
            onSelectView={handleSelectView}
            onSaveView={handleSaveView}
            onDeleteView={handleDeleteView}
            onSetDefaultView={handleSetDefaultView}
            canSaveViews={!!user?.id}
          />
        </header>

        {/* Tabs sit on the same rule as the toolbar rather than in a block below it. */}
        <div
          role="tablist"
          aria-label="Ansicht"
          className="mb-4 flex flex-wrap items-center gap-1 border-b border-gray-200 dark:border-gray-700"
        >
          {TABS.map((label, i) => {
            const isActive = activeTab === i;
            return (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => {
                  setActiveTab(i);
                  if (i !== 3) setSelectedRepId(null);
                }}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isActive
                    ? "border-blue-600 text-blue-600 dark:border-blue-500 dark:text-blue-500"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {error ? (
          <Alert color="failure" icon={HiExclamation} className="mb-4">
            <span className="font-medium">Metriken konnten nicht geladen werden.</span>{" "}
            {error}
          </Alert>
        ) : null}

        {unpriced > 0 ? (
          <Alert color="warning" icon={HiInformationCircle} className="mb-4">
            {formatNumber(unpriced)} Verträge im Zeitraum haben keinen hinterlegten
            Provisionssatz und zählen mit 0&nbsp;€. Die Umsatzzahlen sind entsprechend zu
            niedrig.
          </Alert>
        ) : null}

        {!data && loading ? <MetricsSkeleton /> : null}

        {data ? (
          /* Hold the previous render at reduced opacity during a refetch: no skeleton
             flash, no layout jump. */
          <div className={loading ? "viz-stale" : undefined}>
            {activeTab === 0 ? (
              <CompanyTab
                company={company}
                teams={teams}
                timeseries={timeseries}
                grain={filter.grain}
              />
            ) : null}
            {activeTab === 1 ? <TeamsTab teams={teams} /> : null}
            {activeTab === 2 ? <RepsTab reps={reps} /> : null}
            {activeTab === 3 ? (
              <RepDetailTab
                reps={reps}
                selected={selectedRep}
                onSelect={setSelectedRepId}
                company={company}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </NavbarSidebarLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Tab 1: Company                                                              */
/* -------------------------------------------------------------------------- */

function CompanyTab({
  company,
  teams,
  timeseries,
  grain,
}: {
  company: AllMetrics["company"];
  teams: AllMetrics["teams"];
  timeseries: AllMetrics["timeseries"];
  grain: Grain;
}) {
  if (!company) {
    return (
      <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
        Keine Daten im gewählten Zeitraum.
      </p>
    );
  }

  const revenueSpark = timeseries.slice(-12).map((p) => p.revenue);
  const doorsSpark = timeseries.slice(-12).map((p) => p.recordedDoors);

  const goalRatio =
    company.forecastValue > 0 && company.revenue > 0
      ? company.revenue / company.forecastValue
      : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Hero sits INSIDE the KPI row rather than above it: it is one number among
          six, so giving it a full-width band pushed everything else below the fold.
          It keeps visual primacy through span + type size, not through isolation. */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4 lg:gap-3 xl:grid-cols-7">
        <div className="col-span-2 flex flex-col justify-center rounded-lg border border-gray-100 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 md:p-4">
          <HeroFigure
            label="Umsatz im Zeitraum"
            value={formatCurrency(company.revenue)}
            caption={
              company.forecastValue > 0
                ? `Prognose: ${formatCurrency(company.forecastValue)}`
                : undefined
            }
          />
        </div>

        <StatTile
          label="Verträge"
          value={formatNumber(company.contractsSigned)}
          sparkline={timeseries.slice(-12).map((p) => p.contractsSigned)}
        />
        <StatTile
          label="Abschlussrate"
          value={formatRate(company.closeRate)}
          caption={`${formatNumber(company.closingCustomers)} von ${formatNumber(company.recordedDoors)} Türen`}
        />
        <StatTile
          label="Umsatz / Tür"
          value={company.revenuePerDoor === null ? "—" : formatCurrency(company.revenuePerDoor)}
          sparkline={revenueSpark}
        />
        <StatTile
          label="Verträge / Kunde"
          value={
            company.contractsPerClose === null
              ? "—"
              : company.contractsPerClose.toLocaleString("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
          }
          caption={`Ø ${formatCurrency(company.avgContractValue)}`}
        />
        <StatTile
          label="Aktive Vertriebler"
          value={formatNumber(company.activeReps)}
          sparkline={doorsSpark}
        />
      </div>

      {/* One row, 2:1:1 - the trend earns half the width, the two supporting reads
          a quarter each. Revenue and rates still never share an axis. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-3">
        <div className="lg:col-span-2">
          <ChartWithTable
            level="paired"
            title="Umsatzverlauf"
            subtitle="Ein Wert pro Zeitraum."
            chart={<TrendChart points={timeseries} grain={grain} measure="revenue" />}
            table={<TimeseriesTable points={timeseries} />}
          />
        </div>

        <ChartCard
          level="paired"
          title="Funnel"
          subtitle="Nur erfasste Türen; letzte Stufe zählt Kunden, nicht Verträge."
        >
          <FunnelChart
            baseLabel="erfasste Türen"
            stages={[
              { label: "Erfasste Türen", value: company.recordedDoors, rate: null },
              { label: "Kontaktiert", value: company.contacted, rate: company.contactRate },
              { label: "Interessiert", value: company.interested, rate: company.interestRate },
              { label: "Abgeschlossen (Kunden)", value: company.closingCustomers, rate: company.closeRate },
            ]}
          />
        </ChartCard>

        <ChartCard level="paired" title="Umsatzanteil nach Produktlinie">
          <ProductMixBar teams={teams} />
        </ChartCard>
      </div>

      {goalRatio !== null ? (
        <ChartCard
          level="paired"
          title="Prognose-Erreichung"
          subtitle="Bisheriger Umsatz gegen die gespeicherte Monatsprognose."
        >
          <Meter
            label="Anteil der Prognose erreicht"
            ratio={goalRatio}
            caption={`${formatCurrency(company.revenue)} von ${formatCurrency(company.forecastValue)}`}
          />
        </ChartCard>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tab 2: Product lines                                                        */
/* -------------------------------------------------------------------------- */

function TeamsTab({ teams }: { teams: AllMetrics["teams"] }) {
  const perRepMedian = median(teams.map((t) => t.revenuePerRep));

  const deltaRows = teams
    .filter((t) => t.revenuePerRep !== null)
    .map((t) => ({
      label: t.productLine,
      delta: (t.revenuePerRep ?? 0) - (perRepMedian ?? 0),
    }));

  return (
    <div className="flex flex-col gap-4">
      <ChartCard
        title="Produktlinien-Scorecard"
        subtitle="Pro Vertriebler normalisiert – Teamgrößen unterscheiden sich stark."
      >
        <TeamScorecard teams={teams} />
      </ChartCard>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-3">
        <ChartWithTable
          level="paired"
          title="Umsatz je Vertriebler"
          subtitle="Vergleichbare Kennzahl statt Gesamtumsatz, der nur die Teamgröße abbildet."
          chart={
            <RankedBar
              rows={teams.map((t) => ({ label: t.productLine, value: t.revenuePerRep ?? 0 }))}
              format={(v) => formatCurrency(v)}
            />
          }
          table={<TeamScorecard teams={teams} />}
        />

        <ChartWithTable
          level="paired"
          title="Abweichung vom Median"
          subtitle="Umsatz je Vertriebler gegenüber dem Median aller Produktlinien."
          chart={<DivergingBar rows={deltaRows} format={(v) => formatCurrency(v)} />}
          table={<TeamScorecard teams={teams} />}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tab 3: Reps                                                                 */
/* -------------------------------------------------------------------------- */

function RepsTab({ reps }: { reps: RepMetrics[] }) {
  // Only reps who log their whole funnel can be compared on conversion.
  const ranked = reps.filter(
    (r) => r.recordedDoors >= MIN_DOORS_FOR_RANKING && !isCloseRateArtifact(r)
  );
  const closeMedian = median(ranked.map((r) => r.closeRate));
  const artifactCount = reps.filter(isCloseRateArtifact).length;

  // Coaching targets: enough activity to judge, but conversion below the median.
  const coachingTargets = ranked.filter(
    (r) => closeMedian !== null && r.closeRate !== null && r.closeRate < closeMedian
  );

  return (
    <div className="flex flex-col gap-4">
      {coachingTargets.length > 0 ? (
        <Alert color="info" icon={HiInformationCircle}>
          {coachingTargets.length} Vertriebler liegen bei ausreichender Aktivität unter
          der medianen Abschlussrate ({formatRate(closeMedian)}) – die naheliegendsten
          Coaching-Kandidaten.
        </Alert>
      ) : null}

      {artifactCount > 0 ? (
        <Alert color="warning" icon={HiExclamation}>
          {artifactCount} von {reps.length} Vertrieblern erfassen offenbar nur
          Abschlüsse statt jeder besuchten Tür (Abschlussrate ≈ 100 %). Deren
          Funnel-Raten sind nicht mit denen der übrigen vergleichbar und bleiben beim
          Median unberücksichtigt. Umsatzzahlen sind davon nicht betroffen.
        </Alert>
      ) : null}

      <ChartCard
        title="Vertriebler im Vergleich"
        subtitle="Standardsortierung nach Umsatz je Tür – nicht nach Gesamtumsatz."
      >
        <RepTable reps={reps} />
      </ChartCard>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tab 4: Rep detail                                                           */
/* -------------------------------------------------------------------------- */

function RepDetailTab({
  reps,
  selected,
  onSelect,
  company,
}: {
  reps: RepMetrics[];
  selected: RepMetrics | null;
  onSelect: (id: number | null) => void;
  company: AllMetrics["company"];
}) {
  const detailRows = useMemo(() => {
    if (!selected) return [];
    return [
      {
        key: "closeRate",
        label: "Abschlussrate",
        value: formatRate(selected.closeRate),
        benchmark: formatRate(company?.closeRate ?? null),
        explanation:
          "Anteil der erfassten Türen, die zu einem Vertragsabschluss führten (Kunden mit Abschluss / Erfasste Türen).",
      },
      {
        key: "contactRate",
        label: "Kontaktrate",
        value: formatRate(selected.contactRate),
        benchmark: formatRate(company?.contactRate ?? null),
        explanation:
          "Anteil der dokumentierten Türen, bei denen ein persönlicher Kontakt zustande kam (Kontaktiert / Erfasste Türen).",
      },
      {
        key: "interestRate",
        label: "Interessentenquote",
        value: formatRate(selected.interestRate),
        benchmark: formatRate(company?.interestRate ?? null),
        explanation:
          "Anteil der dokumentierten Türen mit bekundetem Interesse oder Rückrufwunsch (Interessiert / Erfasste Türen).",
      },
      {
        key: "revenuePerDoor",
        label: "Umsatz / Tür",
        value: selected.revenuePerDoor === null ? "—" : formatCurrency(selected.revenuePerDoor),
        benchmark: company?.revenuePerDoor == null ? "—" : formatCurrency(company.revenuePerDoor),
        explanation:
          "Durchschnittlich erzielter Provisionsumsatz pro erfasster Haustür (Umsatz / Erfasste Türen).",
      },
      {
        key: "avgContractValue",
        label: "Ø Vertragswert",
        value: formatCurrency(selected.avgContractValue),
        benchmark: formatCurrency(company?.avgContractValue ?? 0),
        explanation:
          "Durchschnittlicher Provisionswert pro abgeschlossenem Vertrag (Umsatz / Verträge).",
      },
      {
        key: "contractsPerClose",
        label: "Verträge / Kunde",
        value:
          selected.contractsPerClose === null
            ? "—"
            : selected.contractsPerClose.toLocaleString("de-DE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
        benchmark:
          company?.contractsPerClose == null
            ? "—"
            : company.contractsPerClose.toLocaleString("de-DE", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
        explanation:
          "Cross-Selling-Tiefe: Durchschnittliche Anzahl abgeschlossener Verträge pro kaufendem Kunden.",
      },
      {
        key: "cancelRate",
        label: "Stornoquote",
        value: formatRate(selected.cancelRate),
        benchmark: formatRate(company?.cancelRate ?? null),
        explanation:
          "Anteil der stornierten Verträge an allen Verträgen im Zeitraum (Storniert / (Unterschrieben + Storniert)).",
      },
      {
        key: "recordedDoors",
        label: "Erfasste Türen",
        value: formatNumber(selected.recordedDoors),
        benchmark:
          company?.activeReps && company.activeReps > 0
            ? formatNumber(Math.round(company.recordedDoors / company.activeReps))
            : "—",
        explanation:
          "Anzahl der vom Vertriebler in der App dokumentierten Haustüren (Benchmark: Unternehmens-Ø je aktivem Vertriebler).",
      },
      {
        key: "contractsSigned",
        label: "Verträge gesamt",
        value: formatNumber(selected.contractsSigned),
        benchmark:
          company?.activeReps && company.activeReps > 0
            ? formatNumber(Math.round(company.contractsSigned / company.activeReps))
            : "—",
        explanation:
          "Gesamtzahl aller vom Vertriebler im Zeitraum abgeschlossenen Verträge (Benchmark: Unternehmens-Ø je aktivem Vertriebler).",
      },
      {
        key: "revenue",
        label: "Umsatz gesamt",
        value: formatCurrency(selected.revenue),
        benchmark:
          company?.activeReps && company.activeReps > 0
            ? formatCurrency(Math.round(company.revenue / company.activeReps))
            : "—",
        explanation:
          "Gesamter generierter Provisionsumsatz aller Verträge im Zeitraum (Benchmark: Unternehmens-Ø je aktivem Vertriebler).",
      },
    ];
  }, [selected, company]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor="rep-select"
          className="text-sm font-medium text-gray-600 dark:text-gray-400"
        >
          Vertriebler
        </label>
        <select
          id="rep-select"
          className="rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white dark:ring-gray-600 sm:min-w-[200px]"
          value={selected?.workerId ?? ""}
          onChange={(e) => onSelect(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Bitte wählen…</option>
          {reps.map((r) => (
            <option key={r.workerId} value={r.workerId}>
              {r.fullName}
            </option>
          ))}
        </select>
      </div>

      {!selected ? (
        <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
          Vertriebler auswählen, um Funnel und Kennzahlen im Vergleich zum
          Unternehmensdurchschnitt zu sehen.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-3">
            <ChartCard
              level="paired"
              title={`Funnel – ${selected.fullName}`}
              subtitle="Nur erfasste Türen."
            >
              <FunnelChart
                baseLabel="erfasste Türen"
                stages={[
                  { label: "Erfasste Türen", value: selected.recordedDoors, rate: null },
                  { label: "Kontaktiert", value: selected.contacted, rate: selected.contactRate },
                  { label: "Interessiert", value: selected.interested, rate: selected.interestRate },
                  { label: "Abgeschlossen (Kunden)", value: selected.closingCustomers, rate: selected.closeRate },
                ]}
              />
            </ChartCard>

            <ChartCard level="paired" title="Im Vergleich zum Unternehmen">
              <div className="flex flex-col gap-4">
                <ComparisonRow
                  label="Abschlussrate"
                  value={formatRate(selected.closeRate)}
                  benchmark={formatRate(company?.closeRate ?? null)}
                  explanation="Anteil der erfassten Türen, die zu einem Vertragsabschluss führten (Kunden mit Abschluss / Erfasste Türen)."
                />
                <ComparisonRow
                  label="Kontaktrate"
                  value={formatRate(selected.contactRate)}
                  benchmark={formatRate(company?.contactRate ?? null)}
                  explanation="Anteil der dokumentierten Türen, bei denen ein persönlicher Kontakt zustande kam (Kontaktiert / Erfasste Türen)."
                />
                <ComparisonRow
                  label="Umsatz / Tür"
                  value={selected.revenuePerDoor === null ? "—" : formatCurrency(selected.revenuePerDoor)}
                  benchmark={
                    company?.revenuePerDoor == null ? "—" : formatCurrency(company.revenuePerDoor)
                  }
                  explanation="Durchschnittlich erzielter Provisionsumsatz pro erfasster Haustür (Umsatz / Erfasste Türen)."
                />
                <ComparisonRow
                  label="Ø Vertragswert"
                  value={formatCurrency(selected.avgContractValue)}
                  benchmark={formatCurrency(company?.avgContractValue ?? 0)}
                  explanation="Durchschnittlicher Provisionswert pro abgeschlossenem Vertrag (Umsatz / Verträge)."
                />

                {selected.revenueGoalMonth ? (
                  <Meter
                    label="Monatsziel"
                    ratio={selected.goalAttainment}
                    caption={`${formatCurrency(selected.revenue)} von ${formatCurrency(selected.revenueGoalMonth)}`}
                  />
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Kein Monatsziel hinterlegt.
                  </p>
                )}
              </div>
            </ChartCard>
          </div>

          <ChartCard
            level="paired"
            title="Detaillierter Kennzahlenvergleich"
            subtitle="Alle Leistungskennzahlen im direkten Vergleich zum Unternehmensdurchschnitt."
          >
            <div className="viz-scroll-x">
              <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <TableHead className="bg-gray-100 dark:bg-gray-700">
                  <TableHeadCell>
                    <MetricTooltip
                      title="Kennzahl"
                      explanation="Bezeichnung der analysierten Vertriebs- und Funnel-Kennzahl."
                    >
                      <span>Kennzahl</span>
                    </MetricTooltip>
                  </TableHeadCell>
                  <TableHeadCell>
                    <MetricTooltip
                      title="Vertriebler-Wert"
                      explanation={`Individuell erreichter Wert von ${selected.fullName} im ausgewählten Zeitraum.`}
                    >
                      <span>{selected.fullName}</span>
                    </MetricTooltip>
                  </TableHeadCell>
                  <TableHeadCell>
                    <MetricTooltip
                      title="Unternehmens-Ø"
                      explanation="Durchschnittlicher Vergleichswert des gesamten Unternehmens im gleichen Zeitraum."
                    >
                      <span>Unternehmens-Ø</span>
                    </MetricTooltip>
                  </TableHeadCell>
                </TableHead>
                <TableBody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {detailRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="whitespace-nowrap p-3 text-sm font-medium text-gray-900 dark:text-white">
                        <MetricTooltip title={row.label} explanation={row.explanation}>
                          <span>{row.label}</span>
                        </MetricTooltip>
                      </TableCell>
                      <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm font-semibold text-gray-900 dark:text-white">
                        {row.value}
                      </TableCell>
                      <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm text-gray-500 dark:text-gray-400">
                        {row.benchmark}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </ChartCard>
        </div>
      )}
    </div>
  );
}

function ComparisonRow({
  label,
  value,
  benchmark,
  explanation,
}: {
  label: string;
  value: string;
  benchmark: string;
  explanation?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <MetricTooltip title={label} explanation={explanation}>
        <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
      </MetricTooltip>
      <span className="flex items-baseline gap-3">
        <span className="viz-tabular text-sm font-semibold text-gray-900 dark:text-white">
          {value}
        </span>
        <span className="viz-tabular text-xs text-gray-500 dark:text-gray-400">
          Ø {benchmark}
        </span>
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function TimeseriesTable({ points }: { points: AllMetrics["timeseries"] }) {
  return (
    <table className="w-full text-left text-sm">
      <thead className="bg-gray-50 dark:bg-gray-700">
        <tr className="text-gray-500 dark:text-gray-400">
          <th className="p-4 font-medium">
            <MetricTooltip
              title="Zeitraum"
              explanation="Zeitabschnitt der Datenpunkte (Tag, Woche oder Monat je nach ausgewählter Granularität)."
            >
              <span>Zeitraum</span>
            </MetricTooltip>
          </th>
          <th className="p-4 font-medium">
            <MetricTooltip
              title="Erfasste Türen"
              explanation="Gesamtzahl der im Zeitraum dokumentierten Kundenkontakte an der Haustür."
            >
              <span>Erfasste Türen</span>
            </MetricTooltip>
          </th>
          <th className="p-4 font-medium">
            <MetricTooltip
              title="Verträge"
              explanation="Gesamtzahl der im jeweiligen Zeitraum unterzeichneten Verträge."
            >
              <span>Verträge</span>
            </MetricTooltip>
          </th>
          <th className="p-4 font-medium">
            <MetricTooltip
              title="Umsatz"
              explanation="Summe des im Intervall erzielten Provisionsumsatzes aller Verträge."
            >
              <span>Umsatz</span>
            </MetricTooltip>
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
        {points.map((p) => (
          <tr
            key={p.bucket}
            className="text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-700"
          >
            <td className="whitespace-nowrap p-4">{p.bucket}</td>
            <td className="viz-tabular whitespace-nowrap p-4">{formatNumber(p.recordedDoors)}</td>
            <td className="viz-tabular whitespace-nowrap p-4">{formatNumber(p.contractsSigned)}</td>
            <td className="viz-tabular whitespace-nowrap p-4">{formatCurrency(p.revenue)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * First-load skeleton, mirroring the real layout so nothing jumps when data lands.
 * Uses the dashboard's animate-pulse / gray-200 vocabulary.
 *
 * Note this is FIRST LOAD only: a refetch holds the previous render at .viz-stale
 * opacity instead, which avoids a skeleton flash and a layout jump.
 */
function MetricsSkeleton() {
  return (
    <div className="animate-pulse" aria-hidden="true">
      {/* Mirrors the real grid: 6-up KPI row (hero spans 2), then the 2:1:1 charts. */}
      <div className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-4 lg:gap-3 xl:grid-cols-7">
        <div className="col-span-2 h-[104px] rounded-lg border border-gray-100 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
          <div className="h-3 w-28 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="mt-3 h-8 w-40 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-[104px] rounded-lg border border-gray-100 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="mt-3 h-6 w-24 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-3">
        <div className="rounded-lg bg-white p-3 shadow-md dark:bg-gray-800 sm:p-4 lg:col-span-2">
          <div className="mb-3 h-4 w-36 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-[180px] w-full rounded bg-gray-200 dark:bg-gray-700 md:h-[220px]" />
        </div>
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="rounded-lg bg-white p-3 shadow-md dark:bg-gray-800 sm:p-4">
            <div className="mb-3 h-4 w-28 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-[180px] w-full rounded bg-gray-200 dark:bg-gray-700 md:h-[220px]" />
          </div>
        ))}
      </div>
    </div>
  );
}
